import { useCallback, useEffect, useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { InviteHistoryTable } from "../../components/InviteHistoryTable";
import type { InviteSummary } from "../../components/InviteHistoryTable";
import { frameworkLabel } from "../../nav/frameworkMeta";
import { theme, card, btnPrimary, inputStyle } from "../../theme";
import { AUDITOR_ROLE_LABELS, BUREAU_ADMIN_NOTE, submitErrorMessage } from "./auditorShared";

// ── Bureau invitations ────────────────────────────────────────────────────
// Bureau admins invite other auditors into the *same* bureau
// (docs/NAVIGATION.md section 6). Every call goes to the bureau-scoped routes
// on /auditor/bureau/invites; GET /invites is not used here. An invitation
// never carries an administrator flag: the first auditor to join a bureau
// without one becomes its administrator, and Team & Access promotes others.

const ROLE_OPTIONS = Object.entries(AUDITOR_ROLE_LABELS);

interface InviteForm {
  name: string;
  email: string;
  auditorRole: string;
  frameworkIds: string[];
}

const EMPTY_FORM: InviteForm = { name: "", email: "", auditorRole: "auditor", frameworkIds: [] };

/** Row actions surface `payload.error` as-is; translate the API's codes into sentences first. */
function rethrowRowError(err: unknown): never {
  if (err instanceof ApiError) {
    if (err.code === "already_redeemed") throw new ApiError(err.status, { error: "This invitation has already been accepted." });
    if (err.status === 404) throw new ApiError(err.status, { error: "This invitation no longer exists." });
    if (err.status === 403) throw new ApiError(err.status, { error: "Bureau administrator access required." });
  }
  throw err;
}

/** InviteHistoryTable `onResend` for the bureau-scoped route (shared with Team & Access). */
export function resendBureauInvite(id: string): Promise<unknown> {
  return api.post(`/auditor/bureau/invites/${id}/resend`, {}).catch(rethrowRowError);
}

/** InviteHistoryTable `onRevoke` for the bureau-scoped route (shared with Team & Access). */
export function revokeBureauInvite(id: string): Promise<unknown> {
  return api.del(`/auditor/bureau/invites/${id}`).catch(rethrowRowError);
}

const labelStyle: CSSProperties = { fontSize: 11, fontWeight: 600, color: theme.color.muted, marginBottom: 4, display: "block" };

export function AuditorInvitationsPage() {
  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [accredited, setAccredited] = useState<string[]>([]);
  const [form, setForm] = useState<InviteForm>(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState("");
  const [sentTo, setSentTo] = useState("");

  const load = useCallback(() => {
    setLoadError("");
    return api.get<{ invites: InviteSummary[] }>("/auditor/bureau/invites")
      .then((d) => setInvites(d.invites))
      .catch(() => setLoadError("Failed to load invitations."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // The framework picker offers only the bureau's accredited standards; the
  // API rejects anything else for a Framework Specialist.
  useEffect(() => {
    api.get<{ frameworks: string[] }>("/auditor/frameworks")
      .then((d) => setAccredited(d.frameworks))
      .catch(() => setAccredited([]));
  }, []);

  const isSpecialist = form.auditorRole === "framework_specialist";

  function update<K extends keyof InviteForm>(key: K, value: InviteForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setFormError("");
    setSentTo("");
  }

  function toggleFramework(id: string) {
    update("frameworkIds", form.frameworkIds.includes(id) ? form.frameworkIds.filter((f) => f !== id) : [...form.frameworkIds, id]);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    if (!name || !email) { setFormError("Enter the auditor's name and email address."); return; }
    if (isSpecialist && form.frameworkIds.length === 0) { setFormError("Choose at least one standard for a Framework Specialist."); return; }
    setSending(true);
    setFormError("");
    setSentTo("");
    try {
      const res = await api.post<{ invite: InviteSummary }>("/auditor/bureau/invites", {
        name,
        email,
        auditorRole: form.auditorRole,
        ...(isSpecialist ? { frameworkIds: form.frameworkIds } : {}),
      });
      setInvites((prev) => [res.invite, ...prev.filter((i) => i.id !== res.invite.id)]);
      setSentTo(email);
      setForm(EMPTY_FORM);
    } catch (err: unknown) {
      setFormError(submitErrorMessage(err));
    } finally {
      setSending(false);
    }
  }


  return (
    <div className="page-pad">
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.color.heading }}>Invitations</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: theme.color.subtle }}>
            Invite auditors to join your bureau. Invitations expire after 72 hours; pending and expired ones can be resent with a fresh link or revoked.
          </p>
        </div>

        <form onSubmit={submit} style={{ ...card, display: "flex", flexDirection: "column", gap: 14 }} aria-label="Invite an auditor">
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Invite an auditor</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <div>
              <label htmlFor="invite-name" style={labelStyle}>Full name</label>
              <input id="invite-name" style={inputStyle} value={form.name} maxLength={100} autoComplete="off" onChange={(e) => update("name", e.target.value)} placeholder="Alex Auditor" />
            </div>
            <div>
              <label htmlFor="invite-email" style={labelStyle}>Email address</label>
              <input id="invite-email" type="email" style={inputStyle} value={form.email} autoComplete="off" onChange={(e) => update("email", e.target.value)} placeholder="alex@bureau.example" />
            </div>
            <div>
              <label htmlFor="invite-role" style={labelStyle}>Role</label>
              <select id="invite-role" style={inputStyle} value={form.auditorRole} onChange={(e) => update("auditorRole", e.target.value)}>
                {ROLE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>

          {isSpecialist && (
            <div>
              <div style={labelStyle}>Standards this specialist may audit</div>
              {accredited.length === 0 ? (
                <div style={{ fontSize: 12, color: theme.color.faint }}>
                  Your bureau has no accredited standards yet. Set them in Bureau Profile before inviting a Framework Specialist.
                </div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} role="group" aria-label="Standards">
                  {accredited.map((fw) => {
                    const active = form.frameworkIds.includes(fw);
                    return (
                      <button
                        key={fw}
                        type="button"
                        aria-pressed={active}
                        onClick={() => toggleFramework(fw)}
                        style={{
                          fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 3, cursor: "pointer",
                          background: active ? theme.color.successBg : theme.color.bg,
                          color: active ? theme.color.successText : theme.color.muted,
                          border: `1px solid ${active ? theme.color.success : theme.color.border}`,
                        }}
                      >
                        {frameworkLabel(fw)}
                      </button>
                    );
                  })}
                </div>
              )}
              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 6 }}>
                A specialist sees only the standards granted here; you can change them later in Team & Access.
              </div>
            </div>
          )}

          <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{BUREAU_ADMIN_NOTE}</div>

          {formError && <div role="alert" style={{ fontSize: 12.5, color: theme.color.dangerText }}>{formError}</div>}
          {sentTo && <div role="status" style={{ fontSize: 12.5, color: theme.color.successText }}>Invitation sent to {sentTo}.</div>}

          <div>
            <button type="submit" disabled={sending} style={{ ...btnPrimary, opacity: sending ? 0.6 : 1 }}>
              {sending ? "Sending" : "Send invitation"}
            </button>
          </div>
        </form>

        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>Sent invitations</div>
          {loading ? (
            <div style={{ fontSize: 13, color: theme.color.subtle }}>Loading</div>
          ) : loadError ? (
            <div style={{ fontSize: 13, color: theme.color.dangerText }}>
              {loadError}{" "}
              <button type="button" onClick={() => { setLoading(true); load(); }} style={{ background: "none", border: "none", color: theme.color.accentText, cursor: "pointer", fontSize: 13, padding: 0 }}>Retry</button>
            </div>
          ) : (
            <InviteHistoryTable invites={invites} onResent={load} onResend={resendBureauInvite} onRevoke={revokeBureauInvite} />
          )}
        </div>
      </div>
    </div>
  );
}
